// web-app/src/utils/runEndNeighbours.js
//
// How far a run END may travel before it runs into ANOTHER cabinet run.
//
// `runEndRange` stops an end at the wall's end, the minimum span and the run's
// own appliance bays. Nothing stopped it at the run beside it, so a run
// dragged along its wall went straight through its neighbour, or into the
// corner square a run on the next wall already fills. This is that fourth
// bound, and it is measured against what is actually standing in the room
// rather than against the other run's record: the other run's cabinets,
// counters and panels, and the appliances its bays anchor (which carry no
// `runId`).
//
// What MOVES is what stands at the run's end — the last cabinet, the upper
// over it, the counter, and an island's counter overhanging past its end,
// which a bare end line would push an inch into the neighbour. Each is swept
// along the run's own line (`sweptDistance`, the SAT sweep the collision code
// shares) against the neighbours at its own height, and the end may travel as
// far as the first of them reaches. Where nothing stands at the end (a trim
// holds it open), the end line itself is swept, as deep as the run is built.
//
// Two rules keep a run that ALREADY overlaps another from being frozen by it,
// both met on the scan corpus:
//
//   - Height counts. A plan footprint alone reads a wall run's uppers as
//     standing in the way of the island beneath them.
//   - Only what lies AHEAD of the end binds. A scanned island's counter is one
//     object the length of the island, and where the island butts into the
//     perimeter it already overlaps that counter — swept from the island's
//     free end, it would report no room at all.
//
// Anything already touching or inside a neighbour ahead of the end cannot
// grow, and can still shrink.
//
// Who stands what, the ahead filter and the sweep live in
// `@shared-lib/cabinet-runs/neighbourRuns`, because the island fill asks the
// same question: the countertop lip it adds at an open end has to stop at the
// same neighbour this drag stopped at, or saving puts it an inch into the run.

import { FP_EPSILON } from '@shared-lib/collision';
import { doPolygonsOverlap } from '@shared-lib/bathroom-analyzer/pipeline/geometry';
import { getWallAxes } from '@shared-lib/kitchen-assembly/wallMath';
import { runDepthSpec } from '@shared-lib/cabinet-runs/profiles';
import { resolveSegmentDepths } from '@shared-lib/cabinet-runs/freestanding';
import {
  runSolidsByOwner,
  solidsAhead,
  clearAlong,
} from '@shared-lib/cabinet-runs/neighbourRuns';

/**
 * How deep the run is across its own line, as a span along the frame's
 * inward normal: a wall run from the wall face out to its body depth, a
 * free-standing leg its front row ahead of the line and the rest behind it.
 */
function endLineSpan(run, seg) {
  if (!seg?.freestanding) return { lo: 0, hi: runDepthSpec(run) };
  const d = resolveSegmentDepths(seg);
  return { lo: d.front - d.depth, hi: d.front };
}

/** The vertical extent of a set of solids, or null when none is measured. */
function heightOf(solids) {
  const spans = solids.map((s) => s.height).filter(Boolean);
  if (!spans.length) return null;
  return {
    lo: Math.min(...spans.map((h) => h.lo)),
    hi: Math.max(...spans.map((h) => h.hi)),
  };
}

/**
 * The distance the end at `moving` may travel along its frame, `toward` +1
 * (increasing param) or -1, before what stands there touches another run.
 * `null` when nothing of another run lies ahead of it.
 *
 * @param {Object} a
 * @param {Object} a.room
 * @param {Object} a.stored   the stored run (for alias-tolerant ownership)
 * @param {Object} a.run      the rehydrated run (for its depth)
 * @param {Object} a.seg      the end's rehydrated segment
 * @param {Object} a.frame    `segmentFrame(seg)`
 * @param {number} a.moving   the end's param in that frame
 * @param {1|-1}  a.toward    which way the end grows
 * @returns {number|null}
 */
export function runEndNeighbourReach({
  room,
  stored,
  run,
  seg,
  frame,
  moving,
  toward,
}) {
  const axes = frame?.wall ? getWallAxes(frame.wall) : null;
  if (!axes || !Number.isFinite(moving)) return null;
  const { own, neighbours } = runSolidsByOwner(room, stored || run);
  const direction = {
    x: axes.wallDir.x * toward,
    z: axes.wallDir.z * toward,
  };
  const across = (n) => ({
    x: axes.start.x + moving * axes.wallDir.x + n * axes.wallNormal.x,
    z: axes.start.z + moving * axes.wallDir.z + n * axes.wallNormal.z,
  });
  const inWay = solidsAhead(neighbours, across(0), direction);
  if (!inWay.length) return null;

  const span = endLineSpan(run, seg);
  if (!(span.hi > span.lo)) return null;
  const endLine = [across(span.lo), across(span.hi)];
  // What stands at the end: touching the end line, to float noise.
  const atEnd = own.filter((s) =>
    doPolygonsOverlap(endLine, s.footprint, -FP_EPSILON)
  );
  const movers = atEnd.length
    ? atEnd
    : [{ footprint: endLine, height: heightOf(own) }];

  let reach = Infinity;
  for (const mover of movers) {
    reach = Math.min(reach, clearAlong(mover, direction, inWay));
  }
  return Number.isFinite(reach) ? reach : null;
}
